import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { BadgeProps } from '../Badge'
import { Tooltip } from '../Tooltip'
import * as S from './Badges.styles'
import { BadgesHidden } from './BadgesHidden'

export interface BadgesProps {
  badges: BadgeProps[]
  className?: string
  maxDisplayAmount?: number
}

const HIDDEN_BADGE_WIDTH = 32 // Fixed width of the "+N" indicator badge
const BADGE_GAP_SIZE = 6 // Must match the gap value in BadgesContainer styles

const Badges: FC<PropsWithChildren<BadgesProps>> = ({ badges, className, maxDisplayAmount }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const badgeRefs = useRef<(HTMLDivElement | null)[]>([])
  const [visibleCount, setVisibleCount] = useState(badges.length)
  const [previewWidth, setPreviewWidth] = useState(0)

  const calculateVisibleBadges = useCallback(() => {
    if (!containerRef.current || badges.length === 0) {
      setVisibleCount(0)
      setPreviewWidth(0)
      return
    }

    const containerWidth = containerRef.current.offsetWidth
    const badgeWidths = badgeRefs.current.map(badge => badge?.offsetWidth || 0)

    const { totalWidth, visibleBadgeCount } = badgeWidths.reduce(
      (acc, badgeWidth, index) => {
        // Stop adding badges if we've already determined overflow
        if (acc.hasOverflowed) {
          return acc
        }

        const gapWidth = index > 0 ? BADGE_GAP_SIZE : 0
        const widthWithGap = badgeWidth + gapWidth

        // Reserve space for "+N" indicator if there are remaining badges after this one
        const remainingBadges = badgeWidths.length - index - 1
        const needsHiddenIndicator = remainingBadges > 0
        const reservedSpace = needsHiddenIndicator ? HIDDEN_BADGE_WIDTH + BADGE_GAP_SIZE : 0

        // Check if adding this badge would cause overflow
        if (acc.totalWidth + widthWithGap + reservedSpace > containerWidth) {
          return { ...acc, hasOverflowed: true }
        }

        return {
          totalWidth: acc.totalWidth + widthWithGap,
          visibleBadgeCount: acc.visibleBadgeCount + 1,
          hasOverflowed: false,
        }
      },
      { totalWidth: 0, visibleBadgeCount: 0, hasOverflowed: false },
    )

    // We need to have at least one badge visible
    let adjustedVisibleCount = Math.max(1, visibleBadgeCount)

    // Cap at maxDisplayAmount if specified
    if (maxDisplayAmount !== undefined) {
      adjustedVisibleCount = Math.min(adjustedVisibleCount, maxDisplayAmount)
    }

    setVisibleCount(adjustedVisibleCount)

    // Calculate remaining space, accounting for hidden badge width and its gap if badges are hidden.
    // Clamp to >= 0: a stale/zero container measurement during a route transition would otherwise make
    // this negative, blowing up the first badge's `min-width: calc(clientWidth - previewWidth)` and
    // stretching it (which then feeds back through the re-read clientWidth and persists across nav).
    const hasHiddenBadges = adjustedVisibleCount < badges.length
    const hiddenBadgeSpace = hasHiddenBadges ? HIDDEN_BADGE_WIDTH + BADGE_GAP_SIZE : 0
    setPreviewWidth(Math.max(0, containerWidth - totalWidth - hiddenBadgeSpace))
  }, [badges, maxDisplayAmount])

  useLayoutEffect(() => {
    calculateVisibleBadges()
  }, [calculateVisibleBadges])

  const displayedBadges = useMemo(() => badges.slice(0, visibleCount), [badges, visibleCount])

  const hiddenBadges = useMemo(() => badges.slice(visibleCount), [badges, visibleCount])

  const hasHiddenBadges = hiddenBadges.length > 0

  return (
    <S.BadgesContainer className={className} ref={containerRef}>
      {displayedBadges.map((badge, index) => {
        return (
          <Tooltip
            key={`badge-${index}-${badge.dataCy}`}
            content={badge.children}
            placement="top"
            disabled={
              !!badgeRefs.current[index] &&
              (badgeRefs.current[index]?.scrollWidth || 0) <=
                (badgeRefs.current[index]?.clientWidth || 0)
            }
          >
            <S.DisplayedBadge
              {...badge}
              ref={(el: HTMLDivElement | null) => {
                badgeRefs.current[index] = el
              }}
              // First badge can shrink to fit remaining space when there are multiple badges
              // Other badges use their minimum content width
              minWidth={
                index === 0 && badges.length <= 1
                  ? `unset`
                  : index === 0 && badges.length > 1
                    ? `calc(${badgeRefs.current[index]?.clientWidth}px - ${previewWidth}px)`
                    : `min-content`
              }
              // Allow badges to expand to their content width when there are hidden badges
              maxWidth={
                index <= hiddenBadges.length && hiddenBadges.length > 0 ? `max-content` : 'unset'
              }
            />
          </Tooltip>
        )
      })}

      {hasHiddenBadges ? <BadgesHidden hiddenBadges={hiddenBadges} /> : null}
    </S.BadgesContainer>
  )
}

export { Badges }
